<template>
  <nav
    v-if="settings"
    class="sticky inset-x-0 top-0 z-50 w-full transition-all duration-300"
    :class="{ 'bg-white/80 backdrop-blur-md shadow-sm': isScrolled, 'bg-white': !isScrolled }"
  >
    <div class="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16 md:h-20">
        <!-- Logo -->
        <div class="flex-shrink-0 flex items-center">
          <a href="/" class="flex items-center space-x-2 group">
            <span class="text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 group-hover:from-blue-700 group-hover:to-purple-700 transition-all duration-300">
              {{ settings.strings.app_name }}
            </span>
          </a>
        </div>

        <!-- Desktop Menu -->
        <div class="hidden md:block">
          <div class="flex items-baseline space-x-4">
            <a
              v-for="vendor in vendors"
              :key="vendor.id"
              :href="$router.resolve({
                name: 'VendorHome',
                params: {
                  id: vendor.id,
                  slug: vendor.name ? sanitizeTitle(vendor.name) : sanitizeTitle(vendor.slug),
                },
              }).href"
              class="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
            >
              {{ truncate(vendor.name, 15) }}
            </a>
          </div>
        </div>

        <!-- Right Side Icons -->
        <div class="flex items-center space-x-4">
          <!-- Search Icon (Desktop) -->
          <a href="/search" class="p-2 text-gray-500 hover:text-blue-600 transition-colors hidden md:block">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </a>

          <!-- Cart -->
          <a href="/cart" class="relative p-2 text-gray-500 hover:text-blue-600 transition-colors">
            <span class="hidden md:block"><CartPopover /></span>
            <span class="md:hidden">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              <span v-if="cartItemCount > 0" class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-red-600 rounded-full">{{ cartItemCount }}</span>
            </span>
          </a>

          <!-- User Menu / Login -->
          <div v-if="!isLoggedIn">
             <button
              @click="toggleModal"
              class="flex items-center space-x-2 px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              <span class="font-medium text-sm hidden sm:inline">{{ $t("nav.login") }}</span>
            </button>
          </div>

           <!-- Profile Dropdown -->
           <Menu as="div" class="relative" v-else>
            <MenuButton class="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <span class="sr-only">Open user menu</span>
              <img
                class="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                :src="user.photo"
                alt=""
              />
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems class="absolute right-0 w-48 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                 <div class="py-1">
                  <MenuItem v-slot="{ active }">
                    <a href="/profile" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">الملف الشخصي</a>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <a href="/favourites" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">المفضلة</a>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <a href="#" @click="logout" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">تسجيل الخروج</a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>

        </div>
      </div>
    </div>
  </nav>

  <!-- Modern Login Modal -->
  <transition name="fade">
    <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" @click="toggleModal"></div>

      <!-- Modal Panel -->
      <div class="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md w-full transform transition-all">
         <div class="absolute top-4 right-4">
             <button @click="toggleModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                 <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
             </button>
         </div>
        
        <div class="px-8 py-10">
          <div class="text-center mb-8">
            <h3 class="text-2xl font-bold text-gray-900">{{ $t("login.welcome") }}</h3>
            <p class="mt-2 text-sm text-gray-500">سجل دخولك عشان تستمتع بكل خدماتنا</p>
          </div>

          <form @submit.prevent="login" class="space-y-6">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ $t("login.email") }}</label>
              <input
                type="email"
                v-model="details.email"
                id="email"
                class="block w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow sm:text-sm"
                placeholder="name@example.com"
                required
              />
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ $t("login.password") }}</label>
              <input
                type="password"
                v-model="details.password"
                id="password"
                class="block w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow sm:text-sm"
                placeholder="••••••••"
                required
              />
            </div>
            
            <p v-if="message" class="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{{ message }}</p>

            <button
              type="submit"
              class="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-[1.02]"
            >
              {{ $t("nav.login") }}
            </button>
          </form>

          <div class="mt-6 text-center">
             <div class="relative">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-200"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                  <span class="px-2 bg-white text-gray-500">أو</span>
                </div>
              </div>
            <div class="mt-4 text-sm">
                {{ $t("login.dontHaveAnAccount") }}
                <a href="/register" class="font-medium text-blue-600 hover:text-blue-500 hover:underline">
                  {{ $t("login.SignUp") }}
                </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { notify } from "@kyvg/vue3-notification";
import CartPopover from "./CartPopover.vue";

export default {
  data() {
    return {
      isLoggedIn: false,
      vendors: null,
      user: null,
      settings: null,
      showModal: false,
      isScrolled: false,
      details: {
        email: null,
        password: null,
      },
      message: null,
      base_url: this.$store.state.baseUrl,
    };
  },
  components: {
    CartPopover,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    
    this.$store.dispatch("fetchSettings").then(() => {
      this.settings = this.$store.state.settings;
    });

    if (localStorage.getItem("authToken")) {
      this.isLoggedIn = true;
      this.user = JSON.parse(localStorage.getItem("user"));
    }
    
    this.fetchVendors();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  computed: {
    cartItemCount() {
      return this.$store.getters.count;
    },
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 10;
    },
    truncate(str, n) {
      if (!str) return "";
      return str.length > n ? str.substr(0, n - 1) + "..." : str;
    },
    fetchVendors() {
        let selectedLanguage = localStorage.getItem('selectedLanguage') || this.$i18n.locale || 'ar';
        axios
          .get(this.base_url + "api/vendor/types?lan=" + selectedLanguage)
          .then((response) => {
            this.vendors = response.data;
          })
          .catch((error) => console.log(error));
    },
    logout() {
      localStorage.removeItem("authToken");
      localStorage.removeItem("phone");
      localStorage.removeItem("user");
      this.$router.push("/");
      window.location.href = "/login";
    },
    toggleModal() {
      this.showModal = !this.showModal;
    },
    sanitizeTitle(title) {
      if (!title) return "";
      var slug = "";
      var titleLower = title.toLowerCase();
      slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, "e");
      slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, "a");
      slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, "o");
      slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, "u");
      slug = slug.replace(/đ/gi, "d");
      slug = slug.replace(/\s*$/g, "");
      slug = slug.replace(/\s+/g, "-");
      return slug;
    },
    login() {
      this.message = null;
      axios
        .post(this.base_url + "api/login", this.details)
        .then((response) => {
          this.toggleModal();
          if (
            this.settings.strings.otpGateway != "None" &&
            this.settings.strings.enableOTPLogin == "1"
          ) {
             localStorage.setItem("phone", JSON.stringify(response.data.user.phone));
             this.$router.push(this.settings.strings.otpGateway == "Firebase" ? "/firebase/verify/" : "/verify/");
          } else {
            localStorage.setItem("authToken", response.data.token);
            localStorage.setItem("user", JSON.stringify(response.data.user));
            notify({ type: "success", title: "تم الدخول بنجاح", text: "أهلاً بيك من تاني!" });
            window.location.href = "/";
          }
        })
        .catch((error) => {
          if (error.response) {
            this.message = error.response.data.message || "حصلت مشكلة، جرب تاني";
          }
        });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
